import React, { forwardRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import moment from 'moment';

import { DialogControls } from '../../../components/DialogControls/DialogControls';
import { useHideModal } from '../../../actions/modal';
import { saveCheckAltitudeData } from '../../../actions/navigation';
import { NavType } from '../../../components/NavigationRoutes/constants';
import { useStyles } from './useStyles';
import { useMobileWidth } from '../../../components/hooks/useMobileWidth';
import { useForm } from '../../../components/hooks/useForm/useForm';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '../../../components/material-ui/Dialog';
import { FormControl } from '../../../components/material-ui/FormControl';
import { TextField } from '../../../components/material-ui/TextField';
import { CustomTimePicker } from '../../../components/CustomTimePicker/CustomTimePicker';
import { clonePoint, getFields, getFieldType, PointFields } from '../tools';
import { ALTIMETER_WARNING_LIMIT } from '../../../service/constants';
import { Typography } from '../../../components/material-ui/Typography';
import { findPrevCheckAltitude } from '../../../service/NavPointsUtils';
import { getLegPointsSelector } from '../../../selectors/sended-data';
import { useAlert } from '../hooks/useAlert';
import { AlertType } from '../../../reducers/alert';

const fieldList = [
    PointFields.FL,
    PointFields.CAPTAIN_ALTIMETER,
    PointFields.FIRST_OFFICER_ALTIMETER,
    PointFields.STANDBY_ALTIMETER,
    PointFields.DATE_CREATE,
    PointFields.COMMENT,
];

export const CheckAltitude = forwardRef(({ isNewPoint = false, pos, point, toggleUpdate, iUserInit }, ref) => {
    const legId = useSelector((state) => state.screen.currentLeg);
    const startAlert = useAlert(AlertType.ALTITUDE, toggleUpdate);
    const classes = useStyles();
    const { isMobilePortrait } = useMobileWidth();
    const hideModal = useHideModal();
    const dispatch = useDispatch();
    const legPointsSelector = getLegPointsSelector(legId);
    const points = useSelector((state) => legPointsSelector(state), shallowEqual);
    const prevCheckAltitude = findPrevCheckAltitude(points, pos);
    const { t: translate } = useTranslation();
    const handleSuccess = useCallback(
        (data) => {
            dispatch(saveCheckAltitudeData(pos, data, isNewPoint, legId));
            startAlert();
            hideModal();
        },
        [pos, isNewPoint, legId, startAlert]
    );

    const handleClose = useCallback(() => {
        if (!iUserInit) {
            startAlert();
        }

        hideModal();
    }, [iUserInit, startAlert]);

    const handleCheckStatus = useCallback((data, changeStatus) => {
        const { captainAltimeter, firstOfficerAltimeter, fl } = data;
        const altimetersDifferenceWarning =
            captainAltimeter &&
            firstOfficerAltimeter &&
            Math.abs(captainAltimeter - firstOfficerAltimeter) >= ALTIMETER_WARNING_LIMIT &&
            translate('navigationRoute.differenceBetweenAltimeters');
        const incorrectFlightLevelWarning =
            fl &&
            captainAltimeter &&
            Math.abs(fl * 100 - captainAltimeter) >= ALTIMETER_WARNING_LIMIT &&
            translate('navigationRoute.flightLevelNotConsistent');

        const message = `${altimetersDifferenceWarning || ''}${incorrectFlightLevelWarning || ''}`;

        changeStatus((prevState) => ({ ...prevState, message }));
    }, []);

    const fields = useMemo(() => getFields(fieldList), []);

    const { inputs, handleInputChange, handleSubmit, status } = useForm({
        onSuccess: handleSuccess,
        onCheckStatus: handleCheckStatus,
        initialData: {
            ...clonePoint(fieldList, point),
            ...(isNewPoint && {
                ...clonePoint(
                    fieldList.filter((item) => item !== PointFields.FL),
                    points[prevCheckAltitude] || {}
                ),
                isUserAdd: 1,
                pointNumber: point.pointNumber,
                comment: '',
            }),
            type: NavType.ALTITUDE,
            dateCreate: point.dateCreate || isNewPoint ? moment.utc(point.dateCreate) : null,
        },
        fields,
    });

    const dateCreate = get(inputs, 'dateCreate', '');
    const disableSubmit = Boolean(
        fieldList.find((key) => Object.values(status || {}).some((item) => item) || (fields[key].required && !inputs[key]))
    );
    const actions = useMemo(
        () => [
            { label: translate('cancel').toUpperCase(), onClick: handleClose },
            { label: translate('ok'), color: 'primary', type: 'submit', disabled: disableSubmit },
        ],
        [disableSubmit, handleClose]
    );

    return (
        <Dialog ref={ref} open={true} maxWidth="sm" fullScreen={isMobilePortrait}>
            <DialogTitle className={classes.title}>
                {translate(isNewPoint ? 'navigationRoute.addCheckAltitude' : 'navigationRoute.editCheckAltitude')}
            </DialogTitle>
            <form className={classes.fields} onSubmit={handleSubmit}>
                <DialogContent className={classes.content}>
                    <FormControl className={classes.formControl}>
                        <TextField
                            name="fl"
                            label="FL"
                            placeholder="FL"
                            onChange={handleInputChange}
                            type={getFieldType(PointFields.FL)}
                            helperText={status.fl}
                            error={Boolean(status.fl)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth={true}
                            value={get(inputs, 'fl', '')}
                            inputProps={{ className: classes.input }}
                        />
                    </FormControl>
                    <FormControl className={classes.date}>
                        <CustomTimePicker
                            name="dateCreate"
                            label={translate('time')}
                            placeholder={translate('time')}
                            fullWidth={true}
                            helperText={status.dateCreate}
                            error={Boolean(status.dateCreate)}
                            onChange={handleInputChange}
                            style={{ width: '100%' }}
                            value={dateCreate ? moment.utc(dateCreate) : null}
                        />
                    </FormControl>
                    <FormControl className={classes.formControl}>
                        <TextField
                            name="captainAltimeter"
                            label={translate('navigationRoute.captainAltimeter')}
                            placeholder={translate('navigationRoute.captainAltimeter')}
                            onChange={handleInputChange}
                            type={getFieldType(PointFields.CAPTAIN_ALTIMETER)}
                            helperText={status.captainAltimeter}
                            error={Boolean(status.captainAltimeter)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth={true}
                            value={get(inputs, 'captainAltimeter', '')}
                            inputProps={{ className: classes.input }}
                        />
                    </FormControl>
                    <FormControl className={classes.formControl}>
                        <TextField
                            name="standbyAltimeter"
                            label={translate('navigationRoute.standby')}
                            placeholder={translate('navigationRoute.standby')}
                            type={getFieldType(PointFields.STANDBY_ALTIMETER)}
                            helperText={status.standbyAltimeter}
                            error={Boolean(status.standbyAltimeter)}
                            onChange={handleInputChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth={true}
                            value={get(inputs, 'standbyAltimeter', '')}
                            inputProps={{ className: classes.input }}
                        />
                    </FormControl>
                    <FormControl className={classes.formControl}>
                        <TextField
                            name="firstOfficerAltimeter"
                            label={translate('navigationRoute.firstOfficerAltimeter')}
                            placeholder={translate('navigationRoute.firstOfficerAltimeter')}
                            onChange={handleInputChange}
                            type={getFieldType(PointFields.FIRST_OFFICER_ALTIMETER)}
                            helperText={status.firstOfficerAltimeter}
                            error={Boolean(status.firstOfficerAltimeter)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth={true}
                            value={get(inputs, 'firstOfficerAltimeter', '')}
                            inputProps={{ className: classes.input }}
                        />
                    </FormControl>
                    <FormControl className={classes.comment}>
                        <TextField
                            name="comment"
                            label={translate('comment')}
                            onChange={handleInputChange}
                            type={getFieldType(PointFields.COMMENT)}
                            helperText={status.comment}
                            error={Boolean(status.comment)}
                            fullWidth={true}
                            multiline={true}
                            value={get(inputs, 'comment', '')}
                            minRows={1}
                            maxRows={5}
                            className={classes.textarea}
                            inputProps={{ className: classes.input }}
                        />
                    </FormControl>
                    <Typography color="error">{status.message}</Typography>
                </DialogContent>
                <DialogActions className={classes.actions}>
                    <DialogControls actions={actions} />
                </DialogActions>
            </form>
        </Dialog>
    );
});
